import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { getPermissionGuidanceKey, detectPlatform } from '../../utils/platformDetection';
import './FaceGuidelineModal.css';

/** 
 * Analysis provider options
 * - 'gpt-vision': GPT-5.4 Vision (default, detailed analysis)
 * - 'ailabtools': AILabTools API (reliable and fast, legacy)
 */
export type AnalysisProvider = 'ailabtools' | 'gpt-vision';

/** Default analysis provider - GPT Vision for best quality */
export const DEFAULT_ANALYSIS_PROVIDER: AnalysisProvider = 'gpt-vision';

interface FaceGuidelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

export default function FaceGuidelineModal({
  isOpen,
  onClose,
  onCapture,
}: FaceGuidelineModalProps): React.ReactElement | null {
  const { t } = useTranslation('modals');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isNativeMode, setIsNativeMode] = useState<boolean>(false);

  const stopCamera = useCallback((): void => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraReady(false);
  }, []);

  /**
   * Native camera capture using Capacitor Camera plugin
   * Opens the device's native camera app
   */
  const captureWithNativeCamera = useCallback(async (): Promise<void> => {
    try {
      console.log('📸 Using Capacitor Camera for native capture...');
      
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        direction: undefined, // Let user choose front/back
        // Azure Face API recommends faces be at least ~200px for reliable detection.
        // Using 1024x1024 gives ample detail for skin analysis while keeping file size reasonable.
        width: 1024,
        height: 1024,
        correctOrientation: true, // Ensure proper orientation on iOS
      });

      console.log('📷 Photo captured:', { 
        format: photo.format, 
        hasBase64: !!photo.base64String,
        base64Length: photo.base64String?.length || 0 
      });

      if (photo.base64String) {
        // Validate and normalize the format returned by the camera
        const allowedFormats = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'heic', 'heif'] as const;
        const rawFormat = photo.format?.toLowerCase();
        const normalizedFormat =
          rawFormat && allowedFormats.includes(rawFormat as (typeof allowedFormats)[number])
            ? (rawFormat === 'jpg' ? 'jpeg' : rawFormat)
            : 'jpeg';
        const mimeType = `image/${normalizedFormat}`;
        const extension = normalizedFormat === 'jpeg' ? 'jpg' : normalizedFormat;
        const fileName = `face-capture.${extension}`;

        // Convert base64 to File using fetch+blob (more efficient than loop)
        const dataUrl = `data:${mimeType};base64,${photo.base64String}`;
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: mimeType });
        
        console.log('✅ Native camera capture successful:', { 
          fileSize: file.size, 
          fileType: file.type, 
          fileName: file.name 
        });
        onCapture(file);
        onClose();
      } else {
        console.error('❌ No base64String in photo result');
        setError(t('faceCapture.captureError'));
      }
    } catch (err: unknown) {
      const error = err as Error & { code?: string };
      console.error('Native camera error:', error);
      
      // Check if user cancelled
      if (error?.message?.includes('cancel') || error?.code === 'CANCELLED') {
        console.log('📷 User cancelled camera');
        return;
      }
      
      const guidanceKey = getPermissionGuidanceKey();
      const guidance = t(`faceCapture.${guidanceKey}`);
      setError(`${t('faceCapture.permissionRequired')}\n\n${guidance}`);
    }
  }, [onCapture, onClose, t]);

  const startCamera = useCallback(async (): Promise<void> => {
    const platformInfo = detectPlatform();
    console.log('🎥 startCamera called, platformInfo:', JSON.stringify(platformInfo));
    
    // Always try getUserMedia first (works on both web and native WebViews)
    // This gives us the live preview with oval guideline overlay
    console.log('📷 Trying getUserMedia for live preview with guideline...');
    try {
      setError(null);
      setIsNativeMode(false);
      
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not available');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });

      console.log('✅ getUserMedia succeeded, showing live preview');
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setIsCameraReady(true);
        };
      }
    } catch (err) {
      console.warn('⚠️ getUserMedia failed:', err);
      
      // On native platforms, fall back to native camera plugin
      if (platformInfo.isNative) {
        console.log('📱 Falling back to native camera plugin');
        setIsNativeMode(true);
        setIsCameraReady(true);
        return;
      }
      
      // On web, show error with platform-specific guidance
      const guidanceKey = getPermissionGuidanceKey();
      const guidance = t(`faceCapture.${guidanceKey}`);
      setError(`${t('faceCapture.permissionRequired')}\n\n${guidance}`);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, startCamera, stopCamera]);

  const handleCapture = (): void => {
    // For native mode, use Capacitor Camera
    if (isNativeMode) {
      captureWithNativeCamera();
      return;
    }
    
    // For web mode, capture from video stream
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'face-capture.jpg', { type: 'image/jpeg' });
        onCapture(file);
        handleClose();
      }
    }, 'image/jpeg', 0.9);
  };

  const handleClose = (): void => {
    stopCamera();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="face-guideline-modal-overlay" onClick={handleClose}>
      <div className="face-guideline-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('faceCapture.title')}</h3>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="camera-container">
          {error ? (
            <div className="camera-error">
              <p>{error}</p>
            </div>
          ) : isNativeMode ? (
            /* Native mode: Show instructions and capture button */
            <div className="native-camera-prompt">
              <div className="native-camera-icon">📸</div>
              <p className="native-camera-text">{t('faceCapture.nativePrompt')}</p>
              <ul className="native-tips">
                <li>{t('faceCapture.tips.lighting')}</li>
                <li>{t('faceCapture.tips.centered')}</li>
                <li>{t('faceCapture.tips.relaxed')}</li>
              </ul>
            </div>
          ) : (
            /* Web mode: Show live camera preview */
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              {/* Face guideline overlay */}
              <svg className="face-guideline-overlay" viewBox="0 0 640 480">
                {/* Oval face guideline - enlarged for better face alignment */}
                <ellipse
                  cx="320"
                  cy="230"
                  rx="180"
                  ry="230"
                  fill="none"
                  stroke="#00ff00"
                  strokeWidth="3"
                  strokeDasharray="10,5"
                />
                {/* Center crosshair */}
                <line x1="315" y1="220" x2="325" y2="220" stroke="#00ff00" strokeWidth="2" />
                <line x1="320" y1="215" x2="320" y2="225" stroke="#00ff00" strokeWidth="2" />
              </svg>
              {!isCameraReady && (
                <div className="camera-loading">
                  <span className="loading-spinner"></span>
                  <p>{t('faceCapture.loading')}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Only show guideline instruction in web mode where oval is visible */}
        {!isNativeMode && (
          <p className="guideline-instruction">{t('faceCapture.instruction')}</p>
        )}

        <div className="modal-actions">
          <button className="cancel-btn" onClick={handleClose}>
            {t('faceCapture.cancel')}
          </button>
          <button
            className="capture-btn"
            onClick={handleCapture}
            disabled={!isCameraReady || !!error}
          >
            {isNativeMode ? t('faceCapture.openCamera') : t('faceCapture.capture')}
          </button>
        </div>

        {/* Hidden canvas for capturing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}
